import { Outlet, Link, useLocation } from 'react-router-dom';
import { useExamAuth } from '@/hooks/useExamAuth';
import {
  LayoutDashboard,
  Users,
  FileText,
  BookOpen,
  BarChart3,
  Video,
  Settings,
  LogOut,
  Menu,
  X,
  GraduationCap,
  ClipboardCheck,
  Search,
  ChevronDown,
  Award,
  Megaphone,
  BookUp,
  MonitorPlay,
  ScrollText,
  FileBarChart,
  Wrench,
  UserCog,
  Share2,
  TrendingUp,
  Bell,
  LayoutTemplate,
  FileCheck,
  ShieldAlert,
  UsersRound,
  Brain,
  ShieldCheck,
} from 'lucide-react';
import { useState } from 'react';
import { NotificationBell } from '@/components/notifications/NotificationBell';

export const DashboardLayout = () => {
  const { user, logout, hasAnyRole } = useExamAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
  };

  // Define navigation items based on role
  const getNavigation = () => {
    if (!user) return [];

    const commonItems = [
      { name: 'Notifications', href: '/notifications', icon: Bell },
      { name: 'Profile', href: '/profile', icon: Settings },
    ];

    // Tenant admin only (system_admin and super_admin use SystemAdminLayout)
    if (hasAnyRole(['admin', 'tenant_admin'])) {
      return [
        { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
        { name: 'Users', href: '/admin/users', icon: Users },
        { name: 'Announcements', href: '/admin/announcements', icon: Megaphone },
        { name: 'Audit Logs', href: '/admin/audit-logs', icon: ScrollText },
        { name: 'Reports', href: '/admin/reports', icon: FileBarChart },
        { name: 'Bulk Users', href: '/admin/bulk-users', icon: UserCog },
        { name: 'Notification Settings', href: '/admin/notifications', icon: Bell },
        { name: 'Partners', href: '/admin/partners', icon: UsersRound },
        { name: 'AI Models', href: '/admin/ai-models', icon: Brain },
        { name: 'Cert Templates', href: '/admin/certificate-templates', icon: Award },
        { name: 'Settings', href: '/admin/settings', icon: Wrench },
        { name: 'Analytics', href: '/analytics', icon: BarChart3 },
        ...commonItems,
      ];
    }

    if (hasAnyRole(['instructor', 'teacher'])) {
      return [
        { name: 'Dashboard', href: '/teacher/dashboard', icon: LayoutDashboard },
        { name: 'Exams', href: '/teacher/exams', icon: FileText },
        { name: 'Question Banks', href: '/teacher/question-banks', icon: BookOpen },
        { name: 'Questions', href: '/teacher/questions', icon: ClipboardCheck },
        { name: 'Grading', href: '/teacher/grading', icon: GraduationCap },
        { name: 'Gradebook', href: '/teacher/gradebook', icon: BookUp },
        { name: 'Import/Export', href: '/teacher/question-import-export', icon: FileBarChart },
        { name: 'Live Monitor', href: '/teacher/live-monitor', icon: MonitorPlay },
        { name: 'Announcements', href: '/teacher/announcements', icon: Megaphone },
        { name: 'Bank Sharing', href: '/teacher/question-bank-sharing', icon: Share2 },
        { name: 'Certificates', href: '/teacher/certificates', icon: Award },
        { name: 'Exam Results', href: '/teacher/exam-results', icon: FileCheck },
        { name: 'Templates', href: '/teacher/templates', icon: LayoutTemplate },
        { name: 'Student Progress', href: '/teacher/student-progress', icon: TrendingUp },
        { name: 'Violations', href: '/teacher/violation-review', icon: ShieldCheck },
        { name: 'Plagiarism', href: '/teacher/plagiarism', icon: ShieldAlert },
        { name: 'Analytics', href: '/analytics', icon: BarChart3 },
        { name: 'Proctoring', href: '/proctoring/monitor', icon: Video },
        ...commonItems,
      ];
    }

    if (hasAnyRole(['student'])) {
      return [
        { name: 'Dashboard', href: '/student/dashboard', icon: LayoutDashboard },
        { name: 'My Results', href: '/student/results', icon: BarChart3 },
        { name: 'My Certificates', href: '/student/certificates', icon: Award },
        { name: 'Browse Exams', href: '/student/browse-exams', icon: Search },
        ...commonItems,
      ];
    }

    if (hasAnyRole(['proctor'])) {
      return [
        { name: 'Proctoring Monitor', href: '/proctoring/monitor', icon: Video },
        ...commonItems,
      ];
    }

    return commonItems;
  };

  const navigation = getNavigation();

  return (
    <div className="min-h-screen">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 w-52 sidebar-dark transform transition-transform duration-300 ease-in-out z-30 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-white/[0.06]">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm">EX</span>
              </div>
              <span className="text-sm font-semibold text-white/90">Exam System</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white/50 hover:text-white/80 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User info */}
          <div className="px-4 py-4 border-b border-white/[0.06]">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border border-teal-500/30 flex items-center justify-center">
                <span className="text-teal-400 font-medium text-sm">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white/90 truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-white/50 capitalize">
                  {hasAnyRole(['admin']) ? 'Tenant Admin' :
                   hasAnyRole(['instructor', 'teacher']) ? 'Teacher' :
                   hasAnyRole(['proctor']) ? 'Proctor' : 'Student'}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto custom-scrollbar" data-lenis-prevent>
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={isActive ? 'sidebar-item-active' : 'sidebar-item'}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout button */}
          <div className="p-3 border-t border-white/[0.06]">
            <button
              onClick={handleLogout}
              className="w-full sidebar-item hover:text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-52">
        {/* Top bar */}
        <header className="h-16 header-dark sticky top-0 z-10 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-white/60 hover:text-white/90 transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Page title */}
            <div className="hidden sm:block">
              <h2 className="text-lg font-medium text-white/90">
                {navigation.find((item) => location.pathname === item.href)?.name || 'Dashboard'}
              </h2>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Search */}
            <div className="hidden md:flex items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="input-dark pl-9 pr-4 py-1.5 text-sm w-48 lg:w-64"
                />
              </div>
            </div>

            {/* Notifications */}
            <NotificationBell />

            {/* User menu */}
            <div className="flex items-center space-x-2 pl-3 border-l border-white/[0.06]">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border border-teal-500/30 flex items-center justify-center">
                <span className="text-teal-400 font-medium text-xs">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
              <div className="hidden lg:block">
                <p className="text-sm font-medium text-white/90">{user?.first_name}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-white/40" />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
